import React, { useEffect, useState } from 'react';
import { Controller, Control, DeepMap, FieldError } from 'react-hook-form';
import _ from 'lodash';
import Textfield from '@atlaskit/textfield';
import { Field, HelperMessage } from '@atlaskit/form';
import { ErrorMsg } from '../../styles/styles';
import AddsOn from '../AddsOn';
import { emailPattern } from '../../constants/RegexConstants';
import CustomizedLabel from './CustomizedLabel';

type iInputWithController = {
  label: string;
  name: string;
  placeholder?: string;
  //  eslint-disable-next-line
  control: Control<Record<string, any>>;
  defaultValue?: string | number;
  //    eslint-disable-next-line
  onChange: (name: string, value: any, config?: Object) => void;
  prefix?: string;
  suffix?: string;
  helpMessage?: string;
  isDisabled?: boolean;
  testId?: string;
  //    eslint-disable-next-line
  errors?: DeepMap<Record<string, any>, FieldError>;
  isRequired?: boolean;
  isNumeric?: boolean;
  isEmail?: boolean;
  isCompact?: boolean;
  type?: string;
  range?: { min: number; max: number };
  min?: number;
  autoFocus?: boolean;
  isUnitConversion?: boolean;
  triggerOnChangeForDefaultValue?: boolean;
  getUnitConversionSuffix?: (value: string | number) => { suffix: string; qtyString: string | undefined };
  //    eslint-disable-next-line
  externalValidate?: (newValue: any) => any;
  onPreCheck?: (e: React.FocusEvent<HTMLInputElement>) => void;
  ctrlClassName?: string;
};
const InputWithController = ({
  ctrlClassName,
  label,
  name,
  placeholder,
  control,
  defaultValue,
  onChange,
  //  eslint-disable-next-line
  prefix,
  //    eslint-disable-next-line
  suffix,
  helpMessage,
  isDisabled = false,
  testId = 'input-react-hook-form',
  errors,
  isRequired = true,
  isNumeric = false,
  isCompact = false,
  isEmail = false,
  type = 'text',
  range,
  min,
  autoFocus = false,
  isUnitConversion = false,
  triggerOnChangeForDefaultValue = true,
  getUnitConversionSuffix,
  externalValidate,
  onPreCheck,
}: iInputWithController) => {
  const [value, setValue] = useState<string | number>('');
  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    onChange(name, newValue, { shouldValidate: true });
  };
  useEffect(() => {
    setValue(defaultValue || '');
    if (defaultValue && triggerOnChangeForDefaultValue) {
      onChange(name, defaultValue, { shouldValidate: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, triggerOnChangeForDefaultValue]);

  const validateInput = (newValue: string) => {
    // escape , for number
    const escapeComma = newValue?.toString()?.replace(',', '');
    // is required
    if (isRequired && !newValue) {
      return 'is required';
    }
    // is numeric
    if (isNumeric && newValue && Number.isNaN(Number(escapeComma))) {
      return 'must be a number';
    }
    if (isEmail && !emailPattern.test(newValue)) {
      return 'invalid email address';
    }
    if ((min === 0 || min) && newValue && (Number.isNaN(escapeComma) || Number(escapeComma) <= min)) {
      return `must larger than ${min}`;
    }
    // is in a range
    if (
      range &&
      newValue &&
      (Number.isNaN(Number(escapeComma)) || Number(escapeComma) < range.min || Number(escapeComma) > range.max)
    ) {
      return `must be in ${range.min}-${range.max}`;
    }
    if (typeof externalValidate === 'function') {
      return externalValidate(newValue);
    }
    return true;
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!onPreCheck) return;

    onPreCheck(e);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || null}
      rules={{
        validate: newValue => validateInput(newValue),
      }}
      render={ctrlProps => (
        <Field
          key={name}
          name={name}
          label={<CustomizedLabel label={label} htmlFor={testId} isRequired={isRequired} />}
        >
          {({ fieldProps }) => (
            <>
              <Textfield
                // eslint-disable-next-line
                {...ctrlProps}
                // eslint-disable-next-line
                {...fieldProps}
                name={name}
                value={value}
                className={ctrlClassName}
                placeholder={placeholder}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  const val = event.currentTarget.value;
                  handleValueChange(val);
                }}
                elemAfterInput={
                  <AddsOn
                    value={
                      isUnitConversion && typeof getUnitConversionSuffix === 'function'
                        ? getUnitConversionSuffix(value).suffix
                        : suffix
                    }
                  />
                }
                elemBeforeInput={<AddsOn value={prefix} />}
                isDisabled={isDisabled}
                testId={testId}
                isInvalid={_.get(errors, name)}
                isCompact={isCompact}
                type={type}
                autoFocus={autoFocus}
                onBlur={onBlur}
              />
              <HelperMessage>{helpMessage}</HelperMessage>
              {_.get(errors, name) && <ErrorMsg>{_.get(errors, name).message}</ErrorMsg>}
            </>
          )}
        </Field>
      )}
    />
  );
};

export default InputWithController;
